import { useRecordContext } from "ra-core";
import { Badge } from "@/components/ui/badge";
import type { Contact } from "../types";
import { useMarcoActivity, type MarcoActivityEntry } from "./useMarcoActivity";
import {
  isMarcoPermissionError,
  useMarcoAssessments,
  type MarcoAssessmentsData,
} from "./useMarcoAssessments";

/** Format an ISO date string to a readable locale date, or return null. */
function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

type RowProps = { label: string; value: React.ReactNode };

const Row = ({ label, value }: RowProps) => (
  <div className="flex justify-between gap-2 text-sm">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className="text-right font-medium break-all">{value}</span>
  </div>
);

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const ActivityRow = ({ entry }: { entry: MarcoActivityEntry }) => (
  <div className="flex flex-col gap-0.5 border-b py-2 text-sm last:border-b-0">
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium">
        {entry.title}
        {entry.actionable && (
          <span className="text-destructive ml-1 text-xs font-semibold">
            actionable
          </span>
        )}
      </span>
      <span className="text-muted-foreground shrink-0 text-xs">
        {fmtDateTime(entry.occurredAt)}
      </span>
    </div>
    {entry.detail && (
      <span className="text-muted-foreground text-xs">{entry.detail}</span>
    )}
  </div>
);

/**
 * Merged door/delta/enrichment feed — unblocked 2026-09-04 once
 * lead_doors/delta_audit_log were corrected to bigint contact_id. See
 * useMarcoActivity.ts and docs/prd/marco-activity-feed-design.md.
 */
const MarcoActivityFeed = ({ contactId }: { contactId: number }) => {
  const { data: entries, isPending, isError } = useMarcoActivity(contactId);

  if (isPending) return null;

  if (isError) {
    return (
      <p className="text-muted-foreground text-xs italic">
        Couldn't load Marco's activity.
      </p>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <p className="text-muted-foreground text-xs italic">
        No Marco activity yet — run Marco to enrich this contact.
      </p>
    );
  }

  return (
    <div className="mt-1 flex flex-col">
      {entries.map((entry) => (
        <ActivityRow key={entry.id} entry={entry} />
      ))}
    </div>
  );
};

/**
 * Read-only panel surfacing Marco agent fields from public.contacts.
 * No extra fetches — data is already in the record returned by ra-data-postgrest.
 * Renders nothing (returns null) when no Marco fields are populated.
 */
/**
 * MX-MARCO-UI-003: one status row for the current assessment, from the same
 * shared query as the Marco Card. Pending is labelled as not approved.
 */
const AssessmentStatusRows = ({
  data,
  isPending,
  isError,
  error,
}: {
  data: MarcoAssessmentsData | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
}) => {
  if (isPending) return null;

  if (isError) {
    return (
      <Row
        label="Assessment"
        value={
          isMarcoPermissionError(error)
            ? "No permission"
            : "Couldn't load"
        }
      />
    );
  }

  return (
    <>
      {data?.approved && (
        <Row
          label="Assessment"
          value={`Founder-approved · ${data.approved.commercial_priority}`}
        />
      )}
      {data?.pending[0] && (
        <Row
          label="Assessment"
          value={
            <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
              PENDING · proposed {data.pending[0].commercial_priority}
            </Badge>
          }
        />
      )}
    </>
  );
};

export const MarcoIntelligencePanel = () => {
  const record = useRecordContext<Contact>();
  const assessments = useMarcoAssessments(
    record ? Number(record.id) : undefined,
  );
  if (!record) return null;

  const {
    source,
    connection_degree,
    is_connection,
    is_follower,
    reddit_username,
    icp_checked_at,
    suppressed,
    suppression_reason,
    suppressed_until,
    // PRD #16 §E "Right-panel status strip — no overlap": summary-only fields from
    // mentorax.lead_snapshots (via contacts_lead_summary). The tier badge is the
    // one deliberate duplicate with the Marco card — everything else (door
    // reasoning, pains, evidence, score breakdown, draft copy) stays card-only.
    current_tier,
    enriched,
    last_scan_at,
  } = record;

  const hasAny =
    source != null ||
    connection_degree != null ||
    is_connection != null ||
    is_follower != null ||
    reddit_username != null ||
    icp_checked_at != null ||
    suppressed != null ||
    current_tier != null ||
    enriched === true ||
    last_scan_at != null;

  const contactId = Number(record.id);

  if (!hasAny) {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-muted-foreground text-xs italic">
          No Marco profile data yet — run Marco to enrich this contact.
        </p>
        <AssessmentStatusRows
          data={assessments.data}
          isPending={assessments.isPending}
          isError={assessments.isError}
          error={assessments.error}
        />
        <MarcoActivityFeed contactId={contactId} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {current_tier != null && (
        <Row label="Tier" value={<Badge variant="outline">{current_tier}</Badge>} />
      )}
      <Row label="Enriched" value={enriched ? "Yes" : "No"} />
      {last_scan_at != null && (
        <Row label="Last scan" value={fmtDate(last_scan_at)} />
      )}
      {source != null && <Row label="Source" value={source} />}
      {connection_degree != null && (
        <Row
          label="Connection"
          value={
            connection_degree === 1
              ? "1st degree"
              : connection_degree === 2
                ? "2nd degree"
                : `${connection_degree}rd degree`
          }
        />
      )}
      {is_connection != null && (
        <Row label="Connected" value={is_connection ? "Yes" : "No"} />
      )}
      {is_follower != null && (
        <Row label="Follower" value={is_follower ? "Yes" : "No"} />
      )}
      {reddit_username != null && (
        <Row label="Reddit" value={`u/${reddit_username}`} />
      )}
      {icp_checked_at != null && (
        <Row label="ICP checked" value={fmtDate(icp_checked_at)} />
      )}
      {suppressed != null && suppressed && (
        <>
          <Row
            label="Suppressed"
            value={
              <span className="text-destructive font-semibold">Yes</span>
            }
          />
          {suppression_reason != null && (
            <Row label="Reason" value={suppression_reason} />
          )}
          {suppressed_until != null && (
            <Row label="Until" value={fmtDate(suppressed_until)} />
          )}
        </>
      )}
      <AssessmentStatusRows
        data={assessments.data}
        isPending={assessments.isPending}
        isError={assessments.isError}
        error={assessments.error}
      />
      <MarcoActivityFeed contactId={contactId} />
    </div>
  );
};
